import * as Sentry from '@sentry/react';
import Amplitude from 'amplitude-js';
import { useEffect } from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom-v5-compat';

import { history } from 'dashboard/redux/store';

if (import.meta.env.PROD) {
  Amplitude.getInstance().init('8f9fcc63055c35a7ba867e38e0738939');

  const replayIntegration = Sentry.replayIntegration({
    // Redact PII
    maskAllText: true,
    blockAllMedia: true,
    beforeErrorSampling: (error) => {
      return (
        // Record replay only for errors in FLE
        error.transaction?.startsWith(
          '/dashboard/inquiry-templates/:inquiryTemplateId/:inquiryTemplateVersionId',
        ) ?? false
      );
    },
  });

  Sentry.init({
    release: 'persona-web-dashboard@' + import.meta.env.VITE_COMMIT_SHA,
    environment:
      // Some old browsers do not have hostname, e.g. Safari from a few years ago
      window.location.hostname === 'app.withpersona-staging.com' ? 'staging' : 'production',
    dsn: 'https://0425e583857d4e5e938324e9196bbd52@o175220.ingest.sentry.io/5248654',
    // enables automatic browser instrumentation for performance tracking
    integrations: [
      Sentry.reactRouterV5BrowserTracingIntegration({ history }),
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.feedbackIntegration({
        // General
        autoInject: false, // only show for internal users
        showBranding: false,
        colorScheme: 'light',
        useSentryUser: {
          email: 'email',
          name: 'username',
        },
        // Form
        showName: true,
        showEmail: true,
        // Text
        messagePlaceholder:
          'Describe observed behavior, intended behavior, and how to reproduce.\n\nTry disabling your adblocker if you get a submission error.',
        onSubmitSuccess: () => {
          // Submit session replay but continue recording
          replayIntegration?.flush();
        },
      }),
      replayIntegration,
    ],
    // Never auto capture replays. Only capture replays manually for the feedback widget.
    replaysSessionSampleRate: 0,
    // Capture 100% of errors not filtered out by `beforeErrorSampling` callback above
    replaysOnErrorSampleRate: 1,

    tracesSampleRate: 0, // set sample rate to 0 to disable performance monitoring for now
    ignoreErrors: [
      // Network disconnects on iOS throw this error
      'The operation couldn’t be completed. Software caused connection abort',
      // Google maps internal error, not fatal nor in our control
      'Could not load "util"',
      // May be loading errors, no stack trace available -- not caused by our code
      'Unexpected end of input',
      // Google maps vendor library failed to load one of its dependencies. Non-critical path.
      'Could not load "stats".',
      // Benign ResizeObserver error: https://stackoverflow.com/a/50387233
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      // Firefox add-on error https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Errors/Dead_object
      "can't access dead object",
      // Triggers when custom code executed in workflow editor errors
      'evaluateCodeStep',
      // Metamask extension error
      'metamaskSetupProvider',
      // Infrequent react PDF error from latest version https://github.com/wojtekmaj/react-pdf/issues/974
      "Cannot read properties of null (reading 'sendWithPromise')",
      "null is not an object (evaluating 'this.messageHandler.sendWithPromise')",
      // Usually comes from extensions: https://persona-identities-inc.sentry.io/issues/5496861987/?project=5248654
      /^Failed to fetch$/,
    ],
    denyUrls: [
      // Many suggested by Sentry at https://docs.sentry.io/platforms/javascript/configuration/filtering/
      // Facebook flakiness
      /graph\.facebook\.com/i,
      // Facebook blocked
      /connect\.facebook\.net\/en_US\/all\.js/i,
      // Woopra flakiness
      /eatdifferent\.com\.woopra-ns\.com/i,
      /static\.woopra\.com\/js\/woopra\.js/i,
      // Chrome extensions
      /extensions\//i,
      /^chrome:\/\//i,
      // Pendo
      /app.pendo.io/i,
      /cdn.pendo.io/i,
      // Cacaoweb
      /127\.0\.0\.1:4001\/isrunning/i,
      // Other plugins
      /webappstoolbarba\.texthelp\.com\//i,
      /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
      // Google maps
      /maps.googleapis.com/i,
      // Google reCAPTCHA
      /gstatic\.\w+\/recaptcha/i,
    ],
    beforeSend: (event, hint) => {
      return maybeAddRateBasedTag(event, hint);
    },
    beforeSendTransaction(event) {
      /**
       * Sentry will consider transactions that have benign errors like "ResizeObserver loop limit exceeded" to have failed.
       * In order to reduce confusion, we're deleting `internal_error` statuses so we don't get false positives.
       * Any true application errors will be caught by our regular error reporting.
       */

      if (event.contexts?.trace?.status === 'internal_error') {
        delete event.contexts.trace.status;
      }
      return event;
    },
  });
}

function maybeAddRateBasedTag(event: Sentry.ErrorEvent, hint: Sentry.EventHint) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const error = hint.originalException as any;
  const originalMessage = (() => {
    if (error instanceof Error) {
      return error.message;
    } else {
      return error;
    }
  })();

  // This shouldn't happen but we're not in control of all errors,
  // namely those thrown by vendors. Short-circuit WITHOUT modifying.
  if (originalMessage == null || typeof originalMessage.includes !== 'function') {
    return event;
  }

  // TypeErrors are usually valid, so should not be rate based.
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const shouldRateBased = !event.exception?.values[0]?.type?.includes('TypeError');
  if (shouldRateBased) {
    event.tags = {
      ...event.tags,
      rate_based: 'True',
    };
  } else {
    // We must always set the rate_based tag even when false for our alert rules to work correctly.
    event.tags = {
      ...event.tags,
      rate_based: 'False',
    };
  }

  return event;
}
